import { NextRouter } from 'next/router';
import {
  reaction, makeAutoObservable, computed, action,
} from 'mobx';
import { ParsedUrlQuery } from 'querystring';
import { i18n } from 'next-i18next';

import { gulperImpression } from 'src/common/utils/gulperImpressions';
import { PATHS } from '../../common/constants';
import { apiRequest, Requester } from '../../common/utils/apiFetch';
import { SearchQueryParams } from '../types/SearchQuery';
import getSearchUrl from '../utils/getSearchUrl';
import reorderObjectKeys from '../utils/reorderObjectKeys';
import removeEmptyParams from '../utils/removeEmptyParams';
import groupSearchParams from './groupSearchParams';
import AbortableFetch from '../../common/utils/AbortableFetch';
import DatesFieldState from './DatesFieldState';
import GeometryFieldState, { determineGeometry } from './GeometryFieldState';
import GeolocationFieldState from './GeolocationFieldState';
import {
  Breadcrumb,
  Destination,
  SearchResults,
  TopCategory,
} from '../../common/types/api/v4/boats/search';
import { createUrl } from '../../common/utils/url';
import extractCategoryAndLocationSlugs, {
  categoryDictionary,
} from '../utils/extractCategoryAndLocationSlugs';
import appendTrailingSlash from '../../common/utils/appendTrailingSlash';
import getNearbySeoUrl from '../utils/getNearbySeoUrl';
import getCategorySeoUrl from '../utils/getCategorySeoUrl';
import getLocationSeoUrl from '../utils/getLocationSeoUrl';
import { SeoData } from '../../common/types/api/v4/rutter/v1/search/seo';
import FieldState from '../../common/store/FieldState';
import hasGeometry from '../utils/hasGeometry';
import { Geometry } from '../types/Geometry';
import reverseLookupCategoryGroupMapping from '../utils/reverseLookupCategoryGroupMapping';
import removeSeoText from '../utils/removeSeoText';
import type { MiscParams } from '../types/SearchQuery/MiscParams';
import mapCategoriesToGroupIds from '../utils/mapCategoriesToGroupIds';

export const nullOrString = (x: number | string | null): string | null => (x ? `${x}` : null);

type HasBreadcrumbs = {
  breadcrumbs: Breadcrumb[];
};

const hasBreadcrumbs = (x: any): x is HasBreadcrumbs => Array.isArray(x.breadcrumbs)
  && x.breadcrumbs.length > 0
  && typeof x.breadcrumbs[0].slug === 'string'
  && typeof x.breadcrumbs[0].name === 'string';

type HasNearbyDestinations = {
  nearby_destinations: Destination[];
};

// eslint-disable-next-line max-len
const hasNearbyDestinations = (x: any): x is HasNearbyDestinations => Array.isArray(x.nearby_destinations)
  && x.nearby_destinations.length > 0
  && typeof x.nearby_destinations[0].slug === 'string'
  && typeof x.nearby_destinations[0].boat_count === 'number'
  && hasBreadcrumbs(x.nearby_destinations[0]);

type HasTopCategories = {
  top_categories: TopCategory[];
};

const hasTopCategories = (x: any): x is HasTopCategories => Array.isArray(x.top_categories)
  && x.top_categories.length > 0
  && typeof x.top_categories[0].slug === 'string'
  && typeof x.top_categories[0].count === 'number'
  && Boolean(x.top_categories[0].results);

type Boat = SearchResults['results'][number];

type MinimalNextRouter = Pick<NextRouter, 'push' | 'asPath' | 'query' | 'locale'>;

type SearchStoreConstructorProps = {
  router: MinimalNextRouter;
  requester?: Requester;
  initialSearchResults: SearchResults;
  initialSeo: SeoData;
  currency?: string;
  isNearbySearch?: boolean;
};

/**
 * This is store is responsible for managing the search page state
 * - manages all the filters
 * - manages url state
 * - manages the data
 */
class SearchStore {
  /**
   * All query parameters that determine which set of boats will be referenced
   * Excludes:
   * - page: Pagination is handled separately.
   * - currency: This just alters which currency the results are displayed in.
   */
  private get filtersQuery(): Partial<SearchQueryParams> {
    return {
      bareboat: this.bareboat.value,
      capacity: nullOrString(this.capacity.value),
      captained: this.captained.value,
      categories: this.categoryIds,
      category_slug: this.category_slug.value,
      dates: this.dates.query,
      // Only include filters when there are no categories selected
      filters: this.categoryIds.length ? undefined : (this.filters.value || []),
      location: this.location.value,
      min_price: nullOrString(this.min_price.value),
      max_price: nullOrString(this.max_price.value),
      beta: this.beta.value,
      instabooks: this.instabooks.value,
      superowners: this.superowners.value,
      // exclude duration, it's only used for analytics
      // duration: this.duration.value,
      sort: this.sort.value,
    };
  }

  /**
   * Maps category groups to individual category ids
   */
  private get categoryIds(): string[] {
    return mapCategoriesToGroupIds(this.categories.value);
  }

  /**
   * Derives the number of active filters
   */
  public get activeFiltersCount(): number {
    let count = 0;
    // Any number of dates only count as 1
    if (this.dates.query.length) {
      count += 1;
    }
    // Any capacity counts as 1
    if (this.capacity.value) {
      count += 1;
    }
    // Price bounds count as 1
    if (this.min_price.value || this.max_price.value) {
      count += 1;
    }
    // `captained` + `bareboat` count as 1
    if (this.captained.value && this.bareboat.value) {
      count += 1;
    }
    // instabook active counts as 1
    if (this.instabooks.value) {
      count += 1;
    }
    // superowners active counts as 1
    if (this.superowners.value) {
      count += 1;
    }
    // Add 1 for each group category that is selected
    count += this.categories.value.length;

    return count;
  }

  private get geometryQuery(): Partial<SearchQueryParams> {
    return this.geometry.query;
  }

  private get geolocationQuery(): Partial<SearchQueryParams> {
    return this.geolocation.query;
  }

  /**
   * Search api endpoint with the query params included.
   */
  private get apiQuery(): string {
    return getSearchUrl(this.apiQueryObject);
  }

  /**
   * Cleaned up query object for apiQuery.
   */
  private get apiQueryObject(): Record<string, any> {
    // eslint-disable-next-line max-len
    const includeGeometry = !this.placeId.value && !this.location.value && !this.isNearbySearch.value;
    const query = reorderObjectKeys({
      ...this.filtersQuery,
      ...this.geolocationQuery,
      ...(includeGeometry && this.geometryQuery),
      currency: nullOrString(this.currency.value),
      page: this.page.value ? `${this.page.value}` : '1',
      page_size: '24',
      place_id: this.location.value ? null : this.placeId.value,
    });

    return query;
  }

  private getUrl(overrides: Partial<SearchQueryParams> = {}): string {
    /**
     * we need to exclude geometry from the query in 2 cases:
     * - if there is a location slug in the url
     * - if we are using a geoip geometry
     */
    const includeGeometry = !this.location.value && !this.isNearbySearch.value;
    const query = removeEmptyParams({
      ...reorderObjectKeys({
        ...this.filtersQuery,
        ...(includeGeometry && this.geometryQuery),
        page: this.page.value ? `${this.page.value}` : '1',
        ...overrides,
        // don't include category_slug and location in the query params
        category_slug: null,
        location: null,
        currency: null,
        place_id: this.location.value ? null : this.placeId.value,
      }),
      // Append the non-search params at end.
      ...this.nonSearchParams,
    });

    const pathname = appendTrailingSlash(
      `${PATHS.SEARCH + [this.category_slug.value, this.location.value].filter(Boolean).join('/')}`,
    );
    return createUrl({ pathname, query });
  }

  /**
   * A reference to the next router.
   */
  private router: MinimalNextRouter;

  /**
   * Used to preserve non-search params for inclusion in the url.
   */
  private nonSearchParams: ParsedUrlQuery = {};

  /**
   * The url that should be used in the browser.
   * This combines both the query params and non-search params.
   */
  public get url(): string {
    return this.getUrl();
  }

  /**
   * Used to update the router reference.
   */
  public setRouter = (router: MinimalNextRouter): void => {
    this.router = router;
  };

  /**
   * The search api response
   */
  public searchResults: SearchResults = {
    breadcrumbs: [],
    results: [],
    top_reviews: [],
  };

  public setSearchResults = (searchResults: SearchResults): void => {
    this.searchResults = searchResults;
  };

  private searchFetcher: AbortableFetch<SearchResults>;

  private previewSearchFetcher: AbortableFetch<SearchResults>;

  private previewSearchResultsCount: number;

  private onSearchSuccess = (data: SearchResults): void => {
    this.searchResults = data;

    // Set the geometry using results only when there is no geometry.
    // This prevents duplicate api calls when moving the map for the
    // first time on a location page.
    this.settingGeometry = true;
    this.geometry.reset(data.geometry);
    this.placeId.onChange(data.place_id ?? null);
  };

  private onPreviewSearchSuccess = (data: SearchResults): void => {
    this.previewSearchResultsCount = data?.count ?? 0;
  };

  private async callSearchApi(): Promise<void> {
    await this.searchFetcher.fetch(this.apiQuery);
  }

  public seo: SeoData = {};

  public setSeo = (seo: SeoData): void => {
    this.seo = seo;
  };

  private seoFetcher: AbortableFetch<SeoData>;

  private onSeoSuccess = (data: SeoData): void => {
    this.seo = removeSeoText(data);
  };

  private async callSeoApi(): Promise<void> {
    let url = '';
    const location = this.location.value;
    if (location) {
      url = getLocationSeoUrl({ location, category_slug: this.category_slug.value });
    } else if (this.isNearbySearch.value) {
      url = getNearbySeoUrl({ location, category_slug: this.category_slug.value });
    } else {
      url = getCategorySeoUrl({ category_slug: this.category_slug.value });
    }

    await this.seoFetcher.fetch(url);
  }

  public get isLoading(): boolean {
    return this.searchFetcher.isLoading || this.seoFetcher.isLoading;
  }

  public get isPreviewLoading(): boolean {
    return this.previewSearchFetcher.isLoading;
  }

  /**
   * The function that is used to make the api calls.
   */
  private requester: Requester;

  /**
   * The @deprecated field state for the category ids filter.
   */
  public categories: FieldState<string[]>;

  /**
   * The field state for virtual category filters (with fake ids of 1000 and up)
   */
  public filters: FieldState<string[]>;

  /**
   * The field state for the group size filter.
   */
  public capacity: FieldState<number>;

  /**
   * The field state for tracking the selected currency.
   */
  public currency: FieldState<string>;

  /**
   * The max price field state for the price filter.
   */
  public max_price: FieldState<number | null>;

  /**
   * The min price field state for the price filter.
   */
  public min_price: FieldState<number | null>;

  /**
   * The field state for tracking pagination.
   */
  public page: FieldState<number | null>;

  /**
   * The field state for the date filters.
   */
  public dates: DatesFieldState;

  /**
   * The field state for the map geometry.
   */
  public geometry: GeometryFieldState;

  public geolocation: GeolocationFieldState;

  public geolocationEnabled: FieldState<boolean | null>;

  public category_slug: FieldState<string>;

  public location: FieldState<string>;

  public placeId: FieldState<string | null>;

  public beta: FieldState<'true' | 'false' | null>;

  public captained: FieldState<'true' | 'false' | null>;

  public bareboat: FieldState<'true' | 'false' | null>;

  public instabooks: FieldState<'true' | null>;

  public superowners: FieldState<'true' | null>;

  public duration: FieldState<string | null>;

  public sort: FieldState<MiscParams['sort'] | null>;

  /**
   * Used to bypass the reaction that sets the location slug when transitioning
   * from the root search to a location search via the static map.
   */
  private settingGeometry: boolean = false;

  /**
   * Used to bypass the setting of geometry by geoip as done by server
   * geometry is set only internally resulting in a cleaner url
   * Also allows correct SEO call to be made to `/search/seo/nearby/`
   */
  public isNearbySearch: FieldState<boolean>;

  public triggerSearchOnFieldChange: FieldState<boolean>;

  public constructor({
    requester = apiRequest,
    router,
    initialSearchResults,
    initialSeo,
    currency,
    isNearbySearch,
  }: SearchStoreConstructorProps) {
    // By default mobx doesn't decorate these private fields, so we are
    // explicitly declaring how they should be decorated
    type FieldsToDecorate =
      | 'apiQuery'
      | 'filterQuery'
      | 'geometryQuery'
      | 'geolocationQuery'
      | 'nonSearchParams'
      | 'router'
      | 'geometryInUrl'
      | 'hasFiltersApplied'
      | 'onSearchSuccess'
      | 'onSeoSuccess';
    makeAutoObservable<SearchStore, FieldsToDecorate>(this, {
      // The following are private but should be computed properties
      apiQuery: computed,
      filterQuery: computed,
      geometryQuery: computed,
      geolocationQuery: computed,
      geometryInUrl: computed,
      hasFiltersApplied: computed,
      // These field don't need to be observable
      nonSearchParams: false,
      router: false,
      // The following are private but should be actions
      onSearchSuccess: action,
      onSeoSuccess: action,
    });

    this.requester = requester;
    this.router = router;
    this.searchResults = initialSearchResults;
    this.seo = initialSeo;

    this.searchFetcher = new AbortableFetch(this.requester, this.onSearchSuccess);
    this.previewSearchFetcher = new AbortableFetch(this.requester, this.onPreviewSearchSuccess);

    this.seoFetcher = new AbortableFetch(this.requester, this.onSeoSuccess);

    const { searchParams, nonSearchParams } = groupSearchParams(router.asPath);

    this.categories = new FieldState(searchParams.categories ?? []);
    this.filters = new FieldState(searchParams.filters ?? []);
    this.dates = new DatesFieldState(searchParams.dates ?? []);
    this.capacity = new FieldState(searchParams.capacity ? Number(searchParams.capacity) : null);
    this.max_price = new FieldState(searchParams.max_price ? Number(searchParams.max_price) : null);
    this.min_price = new FieldState(searchParams.min_price ? Number(searchParams.min_price) : null);
    this.currency = new FieldState(searchParams.currency ?? currency);
    this.page = new FieldState(searchParams.page ? Number(searchParams.page) : null);
    this.captained = new FieldState(searchParams.captained ?? null);
    this.bareboat = new FieldState(searchParams.bareboat ?? null);
    this.placeId = new FieldState(initialSearchResults.place_id ?? null);
    this.beta = new FieldState(searchParams.beta ?? null);
    this.instabooks = new FieldState(searchParams.instabooks ?? null);
    this.superowners = new FieldState(searchParams.superowners ?? null);
    this.duration = new FieldState(searchParams.duration ?? null);
    this.sort = new FieldState(searchParams.sort ?? null);

    const geometry = determineGeometry(searchParams, this.searchResults.geometry);
    this.geometry = new GeometryFieldState(geometry);

    this.geolocation = new GeolocationFieldState(null);
    this.geolocationEnabled = new FieldState(null);

    const slugs = extractCategoryAndLocationSlugs(this.router.query);
    this.location = new FieldState(slugs.location ?? '');
    this.category_slug = new FieldState(slugs.category_slug ?? '');

    this.nonSearchParams = nonSearchParams;

    const urlHasLocationInformation = (
      slugs.location
      || searchParams.place_id
      || hasGeometry(geometry)
    );

    this.isNearbySearch = new FieldState(isNearbySearch ?? !urlHasLocationInformation);
    this.triggerSearchOnFieldChange = new FieldState(true);

    // Unset the location slug when the map geometry changes
    reaction(
      () => this.geometry.query,
      () => {
        if (this.settingGeometry) {
          this.settingGeometry = false;
        } else if (this.location.value) {
          this.location.onChange('');
        }
      },
    );

    // Unset the category slug when the categories are changed
    reaction(
      () => this.categories.value.length > 0,
      () => {
        if (this.category_slug.value) {
          this.category_slug.onChange('');
        }
      },
    );

    // Reset the pagination when any of the filters change
    reaction(
      () => [this.filtersQuery, this.geolocationQuery],
      () => {
        if (this.resetPagination && this.page.value) {
          this.page.onChange(null);
        } else {
          this.resetPagination = true;
        }
      },
    );

    reaction(
      () => [this.apiQuery, this.currency.value, this.triggerSearchOnFieldChange.value],
      () => {
        if (!this.triggerSearchOnFieldChange.value) return;
        // Update the browser history when the url changes
        this.updateBrowserHistory();

        // Perform a search API call whenever the apiQuery changes
        this.callSearchApi();

        // Unset the active boat when the the api query changes
        this.onActiveBoatChange(null);
      },
    );

    reaction(
      () => [this.duration.value],
      // Update the browser history when the url changes
      () => this.updateBrowserHistory(),
      // Duration is not part of api query, so search api should not be called
    );

    // Perform a seo API call whenever location, category_slug or isNearbySearch changes
    reaction(
      () => [this.location.value + this.category_slug.value + this.isNearbySearch.value],
      () => this.callSeoApi(),
    );

    // Reset min and max price when currency changes
    reaction(
      () => this.currency.value,
      () => {
        this.min_price.onChange(null);
        this.max_price.onChange(null);
      },
    );

    // Track gulper impressions
    reaction(
      () => this.searchResults.results,
      () => gulperImpression(),
    );
  }

  /**
   * Used to temporarily disable resetting the pagination when the state is
   * being updated from the url.
   */
  private resetPagination = true;

  public onPriceChange = (minPrice: number | null, maxPrice: number | null): void => {
    this.min_price.onChange(minPrice);
    this.max_price.onChange(maxPrice);
  };

  public onCaptainedChange = (value: 'true' | 'false' | null): void => {
    if (value === 'true') {
      this.captained.onChange('true');
      this.bareboat.onChange('false');
    } else if (value === 'false') {
      this.bareboat.onChange('true');
      this.captained.onChange('false');
    } else {
      this.bareboat.onChange(null);
      this.captained.onChange(null);
    }
  };

  public async previewFilters(
    overrides: Partial<SearchQueryParams> = {},
  ): Promise<number> {
    let remappedCategories = {};
    if (Array.isArray(overrides.categories)) {
      remappedCategories = {
        categories: mapCategoriesToGroupIds(overrides.categories),
      };
    }
    const params = {
      ...this.apiQueryObject,
      ...overrides,
      ...remappedCategories,
      // Min page size, lower will cause invalid results
      page_size: '5',
    };
    const url = getSearchUrl(params);
    await this.previewSearchFetcher.fetch(url);
    return this.previewSearchResultsCount;
  }

  public onRouteChange = (url: string, query: ParsedUrlQuery): void => {
    if (!url.includes(PATHS.SEARCH)) {
      return;
    }

    // Only parse the values if the url is different
    if (url !== this.url) {
      const { searchParams, nonSearchParams } = groupSearchParams(url);

      this.categories.onChange(reverseLookupCategoryGroupMapping(searchParams.categories ?? []));
      this.dates.reset(searchParams.dates ?? []);
      this.filters.onChange(searchParams.filters ?? []);
      this.capacity.onChange(searchParams.capacity ? Number(searchParams.capacity) : null);
      this.captained.onChange(searchParams.captained ?? null);
      this.bareboat.onChange(searchParams.bareboat ?? null);
      this.max_price.onChange(searchParams.max_price ? Number(searchParams.max_price) : null);
      this.min_price.onChange(searchParams.min_price ? Number(searchParams.min_price) : null);
      this.beta.onChange(searchParams.beta ?? null);
      this.instabooks.onChange(searchParams.instabooks ?? null);
      this.superowners.onChange(searchParams.superowners ?? null);
      this.placeId.onChange(searchParams.place_id ?? null);
      this.duration.onChange(searchParams.duration ?? null);
      this.sort.onChange(searchParams.sort ?? null);

      const slugs = extractCategoryAndLocationSlugs(query);
      this.location.onChange(slugs.location ?? '');
      this.category_slug.onChange(slugs.category_slug ?? '');

      const urlHasLocationInformation = (
        slugs.location
        || searchParams.place_id
        || hasGeometry(searchParams)
      );

      this.isNearbySearch.onChange(!urlHasLocationInformation);

      if (slugs.location) {
        this.placeId.onChange(null);
      } else {
        // Don't update geometry if the url has location
        this.settingGeometry = true;
        const geometry = determineGeometry(searchParams, this.searchResults.geometry);
        this.geometry.reset(geometry);
      }

      // Ensure pagination reaction doesn't reset the page.
      this.resetPagination = false;
      this.page.onChange(searchParams.page ? Number(searchParams.page) : null);

      this.nonSearchParams = nonSearchParams;
    }
  };

  public onCurrencyChange = (currencyCode: string): void => {
    this.currency.onChange(currencyCode);
  };

  public paginationHrefBuilder = (page: number): string => this.getUrl({ page: `${page}` });

  public activeBoat: Boat | null = null;

  public onActiveBoatChange = (boat: Boat | null): void => {
    this.activeBoat = boat;
  };

  public onMapMove = (geometry: Geometry, interaction?: string): void => {
    if (interaction && interaction !== 'resize') {
      this.isNearbySearch.onChange(false);
      this.placeId.onChange(null);
      this.page.onChange(null);
      this.geolocation.onChange(null);
      this.geometry.onChange(geometry);
    }
  };

  public get captainedIsDirty(): boolean {
    return !!(this.captained.value && this.bareboat.value);
  }

  public clearCaptained = (): void => {
    this.onCaptainedChange(null);
  };

  public get superownersIsDirty(): boolean {
    return !!this.superowners.value;
  }

  public clearSuperowners = (): void => this.superowners.onChange(null);

  public get instabooksIsDirty(): boolean {
    return !!this.instabooks.value;
  }

  public clearInstabooks = (): void => this.instabooks.onChange(null);

  public get capacityIsDirty(): boolean {
    return this.capacity.value > 0;
  }

  public clearCapacity = (): void => this.capacity.onChange(null);

  public get categoriesIsDirty(): boolean {
    return this.categories.value.length > 0;
  }

  public clearCategories = (): void => this.categories.onChange([]);

  public get datesIsDirty(): boolean {
    return this.dates.query.length > 0;
  }

  public clearDates = (): void => this.dates.reset([]);

  public get priceIsDirty(): boolean {
    return this.max_price.value > 0 || this.min_price.value > 0;
  }

  public clearPrice = (): void => {
    this.max_price.onChange(null);
    this.min_price.onChange(null);
  };

  public get durationIsDirty(): boolean {
    return !!this.duration.value;
  }

  public clearDuration = (): void => this.duration.onChange(null);

  public clearFilters = (): void => {
    this.clearCapacity();
    this.clearCategories();
    this.clearDates();
    this.clearPrice();
    this.clearCaptained();
    this.clearInstabooks();
    this.clearSuperowners();
    this.clearDuration();
  };

  public get breadcrumbs(): Breadcrumb[] {
    const rootBreadcrumb: Breadcrumb = {
      name: i18n?.t('boat-rental', { ns: 'boat-categories', lng: this.router.locale }) ?? 'Boat Rentals',
      slug: 'boat-rental',
      indexed: true,
    };

    if (hasBreadcrumbs(this.searchResults)) {
      return [rootBreadcrumb].concat(
        this.searchResults.breadcrumbs.filter((breadcrumb) => `/${breadcrumb.slug}/` !== PATHS.SEARCH),
      );
    }
    return [rootBreadcrumb];
  }

  /**
   * Formats the breadcrumbs to a readable location description.
   */
  public get locationDescription(): string {
    if (hasBreadcrumbs(this.searchResults) && this.location.value) {
      const breadcrumbs = this.breadcrumbs.filter((crumb) => crumb.slug !== 'boat-rental').reverse();
      return breadcrumbs.map((u) => u.name).join(', ');
    }
    return '';
  }

  public get nearbyDestinations(): Destination[] {
    if (hasNearbyDestinations(this.seo)) {
      return this.seo.nearby_destinations;
    }
    return [];
  }

  public get topCategory(): TopCategory {
    if (hasTopCategories(this.searchResults)) {
      return this.searchResults.top_categories[0];
    }
    return null;
  }

  /**
   * Determines if a custom title should be constructed on the client side.
   */
  public get isCustomSearch(): boolean {
    return this.geometryInUrl && !this.category_slug.value;
  }

  public get hasFiltersApplied(): boolean {
    return Boolean(
      this.capacity.value
      || this.max_price.value
      || this.min_price.value
      || this.dates.query.length > 0
      || this.categories.value.length > 0
      || this.instabooks.value
      || this.superowners.value
      || (this.captained.value && this.bareboat.value)
      || this.sort.value,
    );
  }

  /**
   * Only include geometry in the url when the current page is
   * neither a nearby page or location page.
   */
  private get geometryInUrl(): boolean {
    return !this.isNearbySearch.value && !this.location.value && hasGeometry(this.geometryQuery);
  }

  /**
   * Determines if the interactive map can be displayed.
   */
  public get hasGeometry(): boolean {
    return hasGeometry(this.geometryQuery);
  }

  /**
   * The categories that are used for custom search titles.
   */
  public get titleCategories(): { id: string; slug: string }[] {
    return this.categories.value.map((id) => categoryDictionary.get(id));
  }

  /**
   * Pages should not be indexed when there is custom geometry, filters, or sort applied.
   */
  public get noIndex(): boolean {
    return this.geometryInUrl || this.hasFiltersApplied || !!this.sort.value;
  }

  /**
   * Update the browser history when the url changes
   */
  private updateBrowserHistory(): void {
    if (this.router.asPath !== this.url) {
      this.router.push(
        this.url,
        this.url,
        { shallow: true },
      );
    }
  }
}

export default SearchStore;
